import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, ReactiveFormsModule } from '@angular/forms'

// PrimeNG
import { CardModule } from 'primeng/card'
import { DatePicker, DatePickerModule } from 'primeng/datepicker'
import { SelectModule } from 'primeng/select'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { ScrollPanelModule } from 'primeng/scrollpanel'

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faRoute } from '@fortawesome/free-solid-svg-icons'

// Models and types
import { UserModel } from '../../../core/models/user.model'
import type { TechnicianWithDistance } from '../../../modules/affairs/services'
import { TechnicianTools } from '../../../core/tools/technician-tools'
import { CalendarWeekTools } from '../../../core/tools/calendar-week-tools'

@Component({
  selector: 'app-technician-selection-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    DatePickerModule,
    SelectModule,
    ProgressSpinnerModule,
    ScrollPanelModule,
    FontAwesomeModule,
  ],
  templateUrl: './technician-selection-panel.component.html',
  styleUrls: ['./technician-selection-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TechnicianSelectionPanelComponent {
  // Icons
  public faRoute = faRoute

  // ViewChild for datepicker (needed for week selection)
  @ViewChild('weekPickerCalendar') weekPickerCalendar: DatePicker | undefined

  // Inputs - Date range
  @Input() rangeDates: FormControl<[Date, Date] | null> = new FormControl(null)

  // Inputs - Technician selection
  @Input() users: UserModel[] = []
  @Input() loadingUsers: boolean = false
  @Input() selectedTechnicianControl: FormControl<UserModel | null> =
    new FormControl(null)

  // Inputs - Closest technicians by route
  @Input() closestTechniciansByRoute: TechnicianWithDistance[] = []
  @Input() loadingTechniciansByRoute: boolean = false

  // Outputs
  @Output() weekSelected = new EventEmitter<[Date, Date]>()
  @Output() technicianSelected = new EventEmitter<UserModel | null>()
  @Output() homeTechnicianClicked = new EventEmitter<UserModel>()

  /**
   * Get technician initials
   */
  getTechnicianInitials(technician: any): string {
    return TechnicianTools.getInitials(technician)
  }

  /**
   * Handle calendar click for week selection
   */
  onCalendarClick(event: MouseEvent): void {
    const result = CalendarWeekTools.handleWeekNumberClick(
      event,
      this.weekPickerCalendar
    )
    if (result) {
      this.rangeDates.setValue(result)
      this.weekSelected.emit(result)
    }
  }

  /**
   * Handle technician selection from dropdown
   */
  onTechnicianSelected(technician: UserModel | null): void {
    this.technicianSelected.emit(technician)
  }

  /**
   * Handle click on a home technician card
   */
  onHomeTechnicianClick(technician: UserModel): void {
    this.homeTechnicianClicked.emit(technician)
  }
}
